import { useCallback } from "react";

import { ContactResult, ContactResultType } from "../../../types/ContactResult";
import { ContactResultRequest, toContactResultRequest } from "../../../types/ContactResultRequest";
import { ContactResultResponse, toDomainContactResult } from "../../../types/ContactResultResponse";
import { DisqualifyingReason, DisqualifyingReasonsType } from "../../../types/DisqualifyingReason";
import { useObx } from "../../../hooks/useObx";
import ApiRequestPaths from "../../../constants/ApiRequestPaths";
import {
  IQualifyStepWrapperConfig,
  IUseCreateNonCandidateContactResultTrigger
} from "../Components/QualifyStepWrapper";

/** A hook to be used for the qualify step to create a disqualifying contact result for the session. */
const useCreateNonCandidateContactResult: IQualifyStepWrapperConfig["UseCreateNonCandidateContactResult"] = ({
  contactId
}) => {
  const { res, err, loading, setReq } = useObx<ContactResultResponse, ContactResultRequest>("Post ContactResult", {
    method: "post",
    url: ApiRequestPaths.postContactResult(contactId)
  });

  const execute = useCallback(
    ({
      patientId,
      patientLeadId,
      disqualificationReason,
      disqualifyingReasons
    }: IUseCreateNonCandidateContactResultTrigger) => {
      const disqualifyingReason = DisqualifyingReason.GetReason(disqualifyingReasons, disqualificationReason);
      const contactResultType =
        disqualificationReason === DisqualifyingReasonsType.ObxBmi
          ? ContactResultType.ObxNonCandidateBmi
          : ContactResultType.ObxNonCandidateMedical;
      setReq(
        r =>
          r && {
            ...r,
            // leadId (domain) → opportunityId (API) via toContactResultRequest
            payload: toContactResultRequest(
              ContactResult.AddDisqualifyingReason(
                new ContactResult(patientId, patientLeadId, contactResultType),
                disqualifyingReason
              )
            )
          }
      );
    },
    []
  );

  return {
    result: res ? toDomainContactResult(res.payload) : null, // opportunityId (API) → leadId (domain)
    isLoading: loading,
    error: Boolean(err),
    execute
  };
};

export default useCreateNonCandidateContactResult;
